/**
 * The TanStack Query client, and the global error policy.
 *
 * **Queries and mutations do not get the same rule, and conflating them is
 * how nine mutations came to fail silently** (DEE-101). The policy lived on
 * the query cache alone, which reads as though it governs the app; it does
 * not. A refused submit, approve, reject or handover changed nothing on
 * screen and said nothing — the button simply looked broken.
 *
 * ## The query rule
 *
 * * **A 422 never toasts.** It produces field errors, next to the fields.
 *   A toast saying "validation failed" while the form shows nothing is the
 *   worst of both.
 * * **A 401 never toasts.** It is a redirect. By the time one surfaces,
 *   `ApiClient` has already tried a refresh and given up.
 *
 * ## The mutation rule, and where it differs
 *
 * 401 is the same — still a redirect. **422 is not.** A mutation carrying a
 * form gets a 422 whose problem document has `errors[]`, and `fieldErrors()`
 * maps those onto the form; that one still belongs next to the fields. But
 * a *bodyless* mutation — `POST .../submit`, `.../approve`,
 * `.../deactivate` — has no form and no field to attach anything to, and
 * its 422 arrives with an empty `errors[]`. Suppressing that one means
 * suppressing the only account the user would ever get.
 *
 * So the mutation rule keys on whether the document actually carries field
 * errors, which is the same thing the form keys on, rather than on the
 * status alone.
 */

import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { ApiError } from "@/lib/api/problem";

/** Queries: a 422 is always a form's business, a 401 is always a redirect. */
export function shouldToastQueryError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  return error.status !== 422 && error.status !== 401;
}

/**
 * Mutations: a 422 is the form's business **only when it names fields**.
 *
 * `fieldErrors()` renders `problem.errors[]` beside the inputs, so a 422
 * that carries them is already being reported. One that carries none — the
 * shape every bodyless action returns — has nowhere else to surface.
 */
export function shouldToastMutationError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  if (error.status === 401) return false;
  if (error.status === 422) return (error.problem.errors ?? []).length === 0;
  return true;
}

function announce(error: unknown): void {
  const problem = error instanceof ApiError ? error.problem : undefined;
  toast.error(problem?.title ?? "Something went wrong", {
    // Every error says what happened *and* what to do next. A bare
    // title fails the copy rule in docs/ui-contract.md.
    description: problem?.detail ?? "The request could not be completed. Try again in a moment.",
    // Errors persist until dismissed; only successes auto-close.
    duration: Number.POSITIVE_INFINITY,
  });
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        if (!shouldToastQueryError(error)) return;
        announce(error);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        // A call site with its own `onError` keeps its own wording — ten of
        // the nineteen have one, several with a fallback message tuned to
        // the action. The cache handler is the floor for the other nine,
        // not a second voice on top of the ten.
        if (mutation.options.onError) return;
        if (!shouldToastMutationError(error)) return;
        announce(error);
      },
    }),
    defaultOptions: {
      queries: {
        // A 503 means a dependency is failing closed. Retrying hammers a
        // system already in an incident, and the user gets a Retry button
        // either way.
        retry: (failureCount, error) => {
          if (error instanceof ApiError) return false;
          return failureCount < 2;
        },
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  });
}
